import './ActualVsPredicted.scss';
import { useEffect, useMemo } from 'react';
import { AutoSizer } from 'react-virtualized-auto-sizer';
import { Area, CartesianGrid, ComposedChart, Line, Tooltip, XAxis, YAxis } from 'recharts';
import {
  dateStringToUtcTimestamp,
  toBpmDateString
} from '@blisspointmedia/bpm-types/dist/FiscalCalendar';
import { CARTESIAN_GRID_STROKE_WIDTH, TICK_STYLE } from '@Components/Charts/ChartConstants';
import { TooltipPassthroughProps } from '@Components/Charts/ChartTooltip/ChartTooltipV2';
import { ActualVsPredictedTooltipContent } from '@Components/Charts/ActualVsPredicted/ActualVsPredicted';
import { useDynamicYAxisWidth } from '@Components/Charts/ChartUtils';
import { useBuildMonthlyXAxis } from '@Components/Charts/MonthlyXAxis/MonthlyXAxis';
import { Brand60, Neutral400, Neutral500 } from '../../../utils/colors';
import { dateToMddyy } from '../../../utils/date-utils';

export interface ActualVsPredictedData {
  actual_kpi: number;
  date: string;
  predicted_kpi: number;
  // Dynamic per-series values (numeric), plus string labels; nullable for gaps.
  [key: string]: number | string | null;
}

interface ActualVsPredictedProps {
  data: ActualVsPredictedData[];
  dateGrouping: string;
  legendValue: string;
  syncId?: number;
  tooltipFormatter?: (value: any) => string;
  yAxisFormatter?: (value: any) => string;
  yAxisStaticWidth?: number;
  setChartYAxisDynamicWidth?: (width: number) => void;
  height?: string;
  width?: string;
  shouldSortTooltipItems?: boolean;
  canUseMonthlyTicks?: boolean;
  tooltipProps?: TooltipPassthroughProps;
}

const RIGHT_MARGIN = 30;
const TOP_MARGIN = 20;

export const ActualVsPredicted = ({
  data,
  dateGrouping,
  legendValue,
  syncId,
  tooltipFormatter,
  yAxisFormatter,
  yAxisStaticWidth,
  setChartYAxisDynamicWidth,
  height = '100%',
  width = '100%',
  shouldSortTooltipItems = true,
  canUseMonthlyTicks = false,
  tooltipProps = {}
}: ActualVsPredictedProps) => {
  const { yAxisWidth, xAxisWidth, setChartRef } = useDynamicYAxisWidth({
    yAxisWidthModifier: x => x + 7,
    reCalcDependency: data
  });

  useEffect(() => {
    if (setChartYAxisDynamicWidth) {
      setChartYAxisDynamicWidth(yAxisWidth);
    }
  }, [setChartYAxisDynamicWidth, yAxisWidth]);

  const dataWithTimestamps = useMemo(
    () =>
      data.map(row => ({
        ...row,
        timestamp: dateStringToUtcTimestamp(toBpmDateString(row?.date))
      })),
    [data]
  );

  const { shouldRenderFallback: shouldRenderFallbackAxis, component: monthlyXAxis } =
    useBuildMonthlyXAxis({
      data: dataWithTimestamps,
      yAxisWidth: yAxisStaticWidth || yAxisWidth,
      axisWidth: xAxisWidth
    });
  const shouldRenderMonthlyTimestamps = canUseMonthlyTicks && !shouldRenderFallbackAxis;

  const defaultXAxis = (
    <XAxis dataKey='date' tick={TICK_STYLE} tickFormatter={val => dateToMddyy(val)} />
  );

  const xAxisComponent = shouldRenderMonthlyTimestamps ? monthlyXAxis : defaultXAxis;

  return (
    <div className='rmmm-actual-vs-predicted-wrapper'>
      <div className='actual-vs-predicted-graph' style={{ height, width }}>
        <AutoSizer
          renderProp={({ width: chartWidth, height: chartHeight }) => (
            <ComposedChart
              data={dataWithTimestamps}
              height={chartHeight ?? 0}
              margin={{ top: TOP_MARGIN, right: RIGHT_MARGIN, left: 0, bottom: 0 }}
              syncId={syncId}
              ref={setChartRef}
              width={chartWidth ?? 0}
            >
              {xAxisComponent}
              <YAxis
                tick={TICK_STYLE}
                tickFormatter={yAxisFormatter}
                tickLine={false}
                axisLine={false}
                width={yAxisStaticWidth || yAxisWidth}
              />
              <Area
                dataKey='actual_kpi'
                fill={Neutral400}
                isAnimationActive={false}
                stroke={Neutral400}
                fillOpacity={1}
              />
              <Line
                dataKey='predicted_kpi'
                dot={false}
                isAnimationActive={false}
                stroke={Brand60}
                strokeWidth={3}
                type='monotone'
              />
              <CartesianGrid
                stroke={Neutral500}
                strokeOpacity={0.25}
                strokeWidth={CARTESIAN_GRID_STROKE_WIDTH}
                vertical={false}
              />
              <Tooltip
                content={
                  <ActualVsPredictedTooltipContent
                    dateGrouping={dateGrouping}
                    tooltipFormatter={tooltipFormatter}
                    shouldSortTooltipItems={shouldSortTooltipItems}
                    tooltipProps={tooltipProps}
                  />
                }
                cursor={{ stroke: Neutral500, width: '1px' }}
                isAnimationActive={false}
              />
            </ComposedChart>
          )}
        />
      </div>
      <div className='right-of-chart'>
        <div className='legend'>
          <div className='legend-item'>
            <div className='square' style={{ backgroundColor: Neutral400 }} />
            <div>{`Actual ${legendValue}`}</div>
          </div>
          <div className='legend-item'>
            <div className='line' />
            <div>{`Predicted ${legendValue}`}</div>
          </div>
        </div>
      </div>
    </div>
  );
};
